import React, { useEffect, useRef } from 'react';

const OrbVisualizer = ({ isActive, mediaStream, isSpeaking = false }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const particlesRef = useRef([]);

  const config = useRef({
    sens: 2.8,
    count: 4000,
    trail: 0.15,
    expand: 1.5,
  });

  useEffect(() => {
    if (!mediaStream) return;

    const setup = async () => {
      try {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        if (sourceRef.current) {
          try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
        }

        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;
        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);

        sourceRef.current = audioContextRef.current.createMediaStreamSource(mediaStream);
        sourceRef.current.connect(analyserRef.current);
      } catch (err) {
        console.error('OrbVisualizer audio setup error:', err);
      }
    };

    setup();

    return () => {
      if (sourceRef.current) {
        try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
        sourceRef.current = null;
      }
    };
  }, [mediaStream]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    const DPR = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      const W = canvas.parentElement.clientWidth;
      const H = canvas.parentElement.clientHeight;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.scale(DPR, DPR);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
    };

    resize();
    window.addEventListener('resize', resize);

    const n = config.current.count;
    particlesRef.current = [];
    for (let i = 0; i < n; i++) {
      particlesRef.current.push({
        a: Math.random() * Math.PI * 2,
        frac: Math.pow(Math.random(), 0.65),
        speed: 0.0001 + Math.random() * 0.0005,
        size: 0.2 + Math.random() * 0.7,
        seed: Math.random() * 200,
        sparkleSpeed: 5 + Math.random() * 10,
      });
    }

    let t = 0;
    let smoothGrave = 0;
    let smoothAgudo = 0;

    const frame = () => {
      t += 0.01;
      const W = canvas.width / DPR;
      const H = canvas.height / DPR;

      ctx.fillStyle = `rgba(2, 2, 2, ${config.current.trail})`;
      ctx.fillRect(0, 0, W, H);

      let grave = 0;
      let agudo = 0;

      if (isActive && analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        for (let i = 0; i < 6; i++) grave += dataArrayRef.current[i];
        for (let i = 80; i < 150; i++) agudo += dataArrayRef.current[i];
        grave /= 6 * 255;
        agudo /= 70 * 255;
      }

      // Gentle idle pulse when active but no audio data
      if (isActive && grave === 0 && agudo === 0) {
        grave = 0.05 + Math.sin(t * 0.5) * 0.03;
        agudo = 0.03 + Math.cos(t * 0.7) * 0.02;
      }

      // When W is speaking, simple gentle pulse
      if (isSpeaking) {
        grave = 0.12 + Math.sin(t * 1.8) * 0.06;
        agudo = 0.08 + Math.cos(t * 1.5) * 0.04;
      }

      // ALWAYS show a minimal orb even when idle/ready (visible breathing animation)
      if (!isActive && !isSpeaking) {
        grave = 0.35 + Math.sin(t * 0.4) * 0.15;
        agudo = 0.25 + Math.cos(t * 0.5) * 0.10;
      }

      smoothGrave += (grave - smoothGrave) * 0.15;
      smoothAgudo += (agudo - smoothAgudo) * 0.15;

      const pulse = smoothGrave * config.current.sens;
      const orbX = W / 2;
      const orbY = H * 0.5;
      const baseR = Math.min(W, H) * 0.18; // Slightly larger base radius
      const radius = baseR * (1 + pulse * config.current.expand);

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';

      for (let i = 0; i < particlesRef.current.length; i++) {
        const p = particlesRef.current[i];
        p.a += p.speed * (1 + pulse * 0.3);

        const noise = Math.sin(t * 0.4 + p.seed) * (1 + pulse * 8);
        const r = p.frac * radius + noise;

        const x = orbX + Math.cos(p.a) * r;
        const y = orbY + Math.sin(p.a) * r;

        const hue = 220 + smoothGrave * 130 - smoothAgudo * 60;
        const flash = Math.sin(t * p.sparkleSpeed + p.seed) > 0.7 ? 1.3 : 0.7;
        const baseOpacity = (!isActive && !isSpeaking) ? 0.5 : 0.2; // Higher opacity when idle for visibility
        const opacity = (baseOpacity + pulse * 0.7) * flash;

        ctx.fillStyle = `hsla(${hue}, 85%, 75%, ${opacity})`;
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, 7);
        ctx.fill();
      }

      ctx.restore();
      animationRef.current = requestAnimationFrame(frame);
    };

    frame();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, isSpeaking]);

  return (
    <div className="relative w-full h-full" data-testid="orb-visualizer">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ touchAction: 'none' }}
      />
    </div>
  );
};

export default OrbVisualizer;
